import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { getBytes, ref } from 'firebase/storage';
import { storage } from '../lib/firebase';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export type PdfDoc = pdfjs.PDFDocumentProxy;

/**
 * Loads a document's original PDF for on-screen rendering.
 *
 * The bytes come straight from Storage under the owner read rule. Nothing about
 * this render path is authoritative — page geometry used for field maths comes
 * from `pageSizes` recorded server-side at upload, not from what pdf.js reports
 * here, so a client-side parser quirk cannot shift where a signature lands.
 */
export async function loadPdf(storagePath: string): Promise<PdfDoc> {
  const bytes = await getBytes(ref(storage, storagePath));
  // pdf.js 6 removed its eval-based font path entirely — that removal IS the fix
  // for GHSA-hq66-cqwq-w95j (arbitrary JS execution from a malicious PDF), which
  // affected 5.6.83–6.2.107 and which this project shipped until the M14 review.
  // There is consequently no isEvalSupported option to set; the CSP withholds
  // 'unsafe-eval' to keep it that way.
  return pdfjs.getDocument({ data: new Uint8Array(bytes), useSystemFonts: false }).promise;
}

/**
 * Loads from a short-lived signed URL.
 *
 * Signers have no Firebase credential, so they cannot use the Storage SDK. The
 * signer API mints a 15-minute signed URL, and only after consent is recorded.
 */
export async function loadPdfFromUrl(url: string): Promise<PdfDoc> {
  return pdfjs.getDocument({ url, useSystemFonts: false }).promise;
}

/** Renders one page into a canvas at `scale`, returning the rendered pixel size. */
export async function renderPage(
  doc: PdfDoc,
  pageIndex: number,
  canvas: HTMLCanvasElement,
  scale: number
): Promise<{ width: number; height: number }> {
  const page = await doc.getPage(pageIndex + 1);
  // Cap DPR: at 3x on a large page this allocates a surprising amount of memory
  // for no visible gain.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale });

  canvas.width = Math.floor(viewport.width * dpr);
  canvas.height = Math.floor(viewport.height * dpr);
  canvas.style.width = `${Math.floor(viewport.width)}px`;
  canvas.style.height = `${Math.floor(viewport.height)}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  await page.render({ canvasContext: ctx, viewport, canvas }).promise;
  return { width: Math.floor(viewport.width), height: Math.floor(viewport.height) };
}
